import { NavLink } from "react-router-dom";
import { Home, MessageCircle, Plus, Compass, User } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { totalUnread } from "@/lib/socialStore";
import { useSocialVersion } from "@/lib/hooks";

const BottomNav = ({ onCreatePress }) => {
    const { user } = useAuth();
    const v = useSocialVersion();
    const msgUnread = user ? totalUnread(user.id) : 0;
    // v is used only to trigger re-render when social stores change
    void v;

    const tabs = [
        { to: "/home", label: "Home", icon: Home, testId: "tab-home" },
        {
            to: "/messages",
            label: "Messages",
            icon: MessageCircle,
            testId: "tab-messages",
            badge: msgUnread,
        },
        { to: null, label: "Create", icon: Plus, testId: "tab-create", isCenter: true },
        {
            to: "/discover",
            label: "Discover",
            icon: Compass,
            testId: "tab-discover",
        },
        { to: "/profile", label: "Profile", icon: User, testId: "tab-profile" },
    ];

    return (
        <nav
            className="glass absolute bottom-0 inset-x-0 z-30 px-2 pt-2 pb-[max(12px,env(safe-area-inset-bottom))]"
            data-testid="bottom-nav"
        >
            <ul className="flex items-end justify-around">
                {tabs.map(({ to, label, icon: Icon, testId, isCenter, badge }) => (
                    <li key={testId} className="flex-1">
                        {isCenter ? (
                            <button
                                data-testid={testId}
                                onClick={onCreatePress}
                                className="group relative w-full flex flex-col items-center justify-end gap-1 py-1.5 select-none text-primary"
                                aria-label="Create"
                            >
                                <span
                                    className="relative flex items-center justify-center h-12 w-12 rounded-2xl -mt-5 text-primary-foreground shadow-[0_12px_32px_-8px_hsl(var(--primary)/0.7)] transition-transform duration-200 group-active:scale-95"
                                    style={{
                                        background:
                                            "linear-gradient(135deg, hsl(var(--primary)) 0%, hsl(var(--primary) / 0.78) 100%)",
                                    }}
                                >
                                    <span
                                        aria-hidden
                                        className="absolute inset-0 rounded-2xl animate-pulse-soft"
                                        style={{
                                            background:
                                                "radial-gradient(circle, hsl(var(--primary) / 0.35), transparent 70%)",
                                        }}
                                    />
                                    <Icon size={22} strokeWidth={2.4} className="relative" />
                                </span>
                                <span className="text-[10.5px] font-medium tracking-tight mt-0.5">
                                    {label}
                                </span>
                            </button>
                        ) : (
                            <NavLink
                                to={to}
                                data-testid={testId}
                                className={({ isActive }) =>
                                    [
                                        "group relative flex flex-col items-center justify-end gap-1 py-1.5 select-none transition-colors duration-200",
                                        isActive
                                            ? "text-primary"
                                            : "text-muted-foreground hover:text-foreground",
                                    ].join(" ")
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        <span
                                            className={[
                                                "relative flex items-center justify-center h-9 w-9 rounded-xl transition-all duration-200",
                                                isActive ? "bg-primary/10" : "bg-transparent group-active:scale-95",
                                            ].join(" ")}
                                        >
                                            <Icon size={21} strokeWidth={isActive ? 2.4 : 2} />
                                            {badge > 0 && (
                                                <span
                                                    data-testid={`${testId}-badge`}
                                                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[9.5px] font-semibold flex items-center justify-center ring-2 ring-background"
                                                >
                                                    {badge > 99 ? "99+" : badge}
                                                </span>
                                            )}
                                        </span>
                                        <span className="text-[10.5px] font-medium tracking-tight">
                                            {label}
                                        </span>
                                    </>
                                )}
                            </NavLink>
                        )}
                    </li>
                ))}
            </ul>
        </nav>
    );
};

export default BottomNav;
